// =============================================================================
// SIREPPAR - Habilitación de Agrupaciones (Convocatoria Gaiman 2026)
// =============================================================================

import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import { convocatoriaService, documentoService } from '../../services/api';
import { Link } from 'react-router-dom';

interface Agrupacion {
  id: string;
  partidoId: string;
  denominacion: string;
  sigla: string | null;
  tipo: string;
  personeria: string;
  localidad: string | null;
  habilitada: boolean;
  motivoDeshabilitacion: string | null;
  actualizadaPor: string | null;
  actualizadaEn: string | null;
  marcadaPor: string | null;
  marcadaEn: string | null;
  estadoLista: string;
  ultimaActividad: string | null;
}

interface Convocatoria {
  id: string;
  denominacion: string;
  fechaActo: string;
  estado: string;
  plazos: Record<string, string>;
  instrumentoTipo: string;
  instrumentoNumero: string;
  autoridadConvocante: string;
  ambitoTipo: string;
  cargo: string;
  cantidadTitulares: number;
  cantidadSuplentes: number;
  sistemaElectoral: string;
  documentoInstrumento: { id: string; nombreOriginal: string } | null;
}

interface PartidoDisponible {
  id: string;
  denominacion: string;
  sigla: string | null;
  tipo: string;
  estado: string;
  localidad: string | null;
  personeriaValida: boolean;
}

const TIPO_LABELS: Record<string, string> = {
  MUNICIPAL: 'Municipal',
  DISTRITO: 'Distrital',
  PROVINCIAL: 'Provincial',
};

const HITOS: Array<{ key: string; label: string }> = [
  { key: 'cierre_candidaturas', label: 'Cierre presentación candidaturas' },
  { key: 'plazo_subsanacion', label: 'Subsanación observaciones' },
  { key: 'resolucion_tep', label: 'Resolución de oficialización TEP' },
  { key: 'audiencia_bus', label: 'Audiencia BUS' },
  { key: 'inicio_campana', label: 'Inicio de campaña' },
];

const AMBITO_LABELS: Record<string, string> = {
  MUNICIPAL: 'Municipio de Gaiman',

  PROVINCIAL: 'Provincial',
  CIRCUNSCRIPCION: 'Circunscripción',
};

const SISTEMA_LABELS: Record<string, string> = {
  PLURALIDAD: 'Simple pluralidad de sufragios',
};

// Las fechas llegan como string ("2026-12-13" o "2026-12-13T00:00:00.000Z").
// Nunca pasar por `new Date(x).toLocaleDateString()`: eso convierte a la hora
// local del navegador y una fecha en medianoche UTC se corre un día para atrás
// en Argentina (UTC-3). Se toma el string tal cual, sin pasar por huso horario.
function formatFechaCorta(fecha?: string | null): string {
  if (!fecha) return '—';
  const [y, m, d] = fecha.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

// Motivos que asigna el sistema (seed / reglas automáticas). Si una fila tiene
// alguno de estos dos, el checkbox queda bloqueado — es una exclusión legal o
// territorial, no una decisión manual que se pueda tocar desde acá.
const MOTIVOS_SISTEMA = [
  'personería no vigente',
  'fuera de ámbito territorial de la convocatoria',
];

// Etiqueta + color de cada estadoLista que devuelve el backend
// (SIN_ACTIVIDAD / BORRADOR_* / o el estado del expediente una vez presentada).
const ESTADO_LISTA_INFO: Record<string, { label: string; color: string }> = {
  SIN_ACTIVIDAD: { label: 'Sin actividad', color: 'bg-gray-100 text-gray-500' },
  BORRADOR_SIN_CANDIDATO: { label: 'Borrador — sin candidato', color: 'bg-amber-100 text-amber-800' },
  BORRADOR_CON_CANDIDATO: { label: 'Borrador — candidato cargado', color: 'bg-amber-100 text-amber-800' },
  PRESENTADO: { label: 'Presentada — pendiente análisis', color: 'bg-blue-100 text-blue-800' },
  EN_ANALISIS: { label: 'Presentada — en análisis', color: 'bg-blue-100 text-blue-800' },
  OBSERVADO: { label: 'Observada', color: 'bg-red-100 text-red-800' },
  EN_SECRETARIO: { label: 'En Secretario', color: 'bg-purple-100 text-purple-800' },
  EN_TEP: { label: 'En el TEP', color: 'bg-indigo-100 text-indigo-800' },
  VIGENTE: { label: 'Oficializada', color: 'bg-green-100 text-green-800' },
  RECHAZADO: { label: 'Rechazada', color: 'bg-gray-200 text-gray-700' },
};

function ambitoBadge(a: Agrupacion): { label: string; color: string } {
  if (a.tipo === 'MUNICIPAL') {
    const esGaiman = (a.localidad || '').toLowerCase().includes('gaiman');
    return esGaiman
      ? { label: 'Municipal Gaiman', color: 'bg-blue-100 text-blue-800' }
      : { label: 'Municipal (otra localidad)', color: 'bg-gray-100 text-gray-600' };
  }
  if (a.tipo === 'DISTRITO') return { label: 'Distrital', color: 'bg-purple-100 text-purple-800' };
  if (a.tipo === 'PROVINCIAL') return { label: 'Provincial', color: 'bg-indigo-100 text-indigo-800' };
  return { label: a.tipo, color: 'bg-gray-100 text-gray-600' };
}

function diasRestantes(fecha?: string | null): number | null {
  if (!fecha) return null;
  const [y, m, d] = fecha.slice(0, 10).split('-').map(Number);
  const destino = new Date(y, m - 1, d).getTime(); // medianoche local, no UTC
  const hoy = new Date().setHours(0, 0, 0, 0);
  return Math.ceil((destino - hoy) / (1000 * 60 * 60 * 24));
}

export default function AgrupacionesHabilitadasPage() {
  const queryClient = useQueryClient();

  const [busqueda, setBusqueda] = useState('');
  const [filtroAmbito, setFiltroAmbito] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');

  const [modalAgrupacion, setModalAgrupacion] = useState<Agrupacion | null>(null);
  const [modalHabilitar, setModalHabilitar] = useState(true);
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);

  const [modalAgregarAbierto, setModalAgregarAbierto] = useState(false);
  const [busquedaModal, setBusquedaModal] = useState('');
  const [agregandoId, setAgregandoId] = useState<string | null>(null);

  const { data: convocatoria, isLoading: cargandoConvocatoria, error: errorConvocatoria } = useQuery<Convocatoria>({
    queryKey: ['convocatoria-actual'],
    queryFn: async () => (await convocatoriaService.getActual()).data.data,
  });

  const { data: agrupaciones, isLoading: cargandoAgrupaciones, error: errorAgrupaciones } = useQuery<Agrupacion[]>({
    queryKey: ['convocatoria-agrupaciones', convocatoria?.id],
    queryFn: async () => (await convocatoriaService.getAgrupaciones(convocatoria!.id)).data.data,
    enabled: !!convocatoria?.id,
  });

  const { data: disponibles, isLoading: cargandoDisponibles } = useQuery<PartidoDisponible[]>({
    queryKey: ['convocatoria-disponibles', convocatoria?.id],
    queryFn: async () => (await convocatoriaService.getDisponibles(convocatoria!.id)).data.data,
    enabled: modalAgregarAbierto && !!convocatoria?.id,
  });

  const cargando = cargandoConvocatoria || cargandoAgrupaciones;
  const error = errorConvocatoria || errorAgrupaciones;

  const contadores = useMemo(() => {
    const lista = agrupaciones || [];
    return {
      total: lista.length,
      habilitadas: lista.filter(a => a.habilitada).length,
      fueraDeAmbito: lista.filter(a => a.motivoDeshabilitacion === 'fuera de ámbito territorial de la convocatoria').length,
      sinPersoneria: lista.filter(a => a.motivoDeshabilitacion === 'personería no vigente').length,
    };
  }, [agrupaciones]);

  // Actividad de las candidaturas: solo tiene sentido para las agrupaciones
  // habilitadas (las demás nunca pudieron iniciar un borrador).
  const habilitadas = useMemo(() => (agrupaciones || []).filter(a => a.habilitada), [agrupaciones]);

  const actividadContadores = useMemo(() => {
    const conBorrador = habilitadas.filter(a => a.estadoLista === 'BORRADOR_SIN_CANDIDATO' || a.estadoLista === 'BORRADOR_CON_CANDIDATO').length;
    const presentadas = habilitadas.filter(a => !['SIN_ACTIVIDAD', 'BORRADOR_SIN_CANDIDATO', 'BORRADOR_CON_CANDIDATO'].includes(a.estadoLista)).length;
    const sinActividad = habilitadas.filter(a => a.estadoLista === 'SIN_ACTIVIDAD').length;
    return { conBorrador, presentadas, sinActividad };
  }, [habilitadas]);

  const listaFiltrada = useMemo(() => {
    return (agrupaciones || []).filter(a => {
      if (busqueda && !a.denominacion.toLowerCase().includes(busqueda.toLowerCase()) && !a.sigla?.toLowerCase().includes(busqueda.toLowerCase())) {
        return false;
      }
      if (filtroAmbito && a.tipo !== filtroAmbito) return false;
      if (filtroEstado === 'habilitadas' && !a.habilitada) return false;
      if (filtroEstado === 'deshabilitadas' && a.habilitada) return false;
      return true;
    });
  }, [agrupaciones, busqueda, filtroAmbito, filtroEstado]);

  const disponiblesFiltrados = useMemo(() => {
    const lista = disponibles || [];
    if (!busquedaModal.trim()) return lista;
    const q = busquedaModal.toLowerCase();
    return lista.filter(p => p.denominacion.toLowerCase().includes(q) || p.sigla?.toLowerCase().includes(q));
  }, [disponibles, busquedaModal]);

  const abrirModal = (a: Agrupacion) => {
    setModalAgrupacion(a);
    setModalHabilitar(!a.habilitada);
    setMotivo('');
  };

  const confirmarCambio = async () => {
    if (!modalAgrupacion || !convocatoria) return;
    if (!modalHabilitar && !motivo.trim()) {
      toast.error('El motivo es obligatorio para deshabilitar');
      return;
    }
    setGuardando(true);
    try {
        await convocatoriaService.actualizarHabilitacion(convocatoria.id, modalAgrupacion.partidoId, {
            habilitada: modalHabilitar,
            motivoDeshabilitacion: motivo.trim() || undefined,
          });
      toast.success(modalHabilitar ? 'Agrupación habilitada' : 'Agrupación deshabilitada');
      queryClient.invalidateQueries({ queryKey: ['convocatoria-agrupaciones', convocatoria.id] });
      setModalAgrupacion(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo actualizar la agrupación');
    } finally {
      setGuardando(false);
    }
  };

  const agregarAgrupacion = async (partido: PartidoDisponible) => {
    if (!convocatoria || !partido.personeriaValida) return;
    setAgregandoId(partido.id);
    try {
      await convocatoriaService.agregarAgrupacion(convocatoria.id, partido.id);
      toast.success(`${partido.denominacion} agregada a la convocatoria`);
      queryClient.invalidateQueries({ queryKey: ['convocatoria-agrupaciones', convocatoria.id] });
      queryClient.invalidateQueries({ queryKey: ['convocatoria-disponibles', convocatoria.id] });
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo agregar la agrupación');
    } finally {
      setAgregandoId(null);
    }
  };

  const cerrarModalAgregar = () => {
    setModalAgregarAbierto(false);
    setBusquedaModal('');
  };

  const plazoCierre = convocatoria?.plazos?.cierre_candidaturas;
  const diasParaCierre = diasRestantes(plazoCierre);

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error al cargar la convocatoria</p>
        <button onClick={() => window.location.reload()} className="mt-4 text-primary-600 hover:text-primary-700">
          Reintentar
        </button>
      </div>
    );
  }

  if (cargando) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!convocatoria) {
    return <div className="text-center py-12 text-red-600">No se encontró ninguna convocatoria cargada.</div>;
  }

  return (
    <div className="space-y-6">
      {/* Cabecera */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">{convocatoria.denominacion}</h1>
        <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-600">
          <span>Acto electoral: {formatFechaCorta(convocatoria.fechaActo)}</span>
          {plazoCierre && (
            <span>
              Cierre de candidaturas: {formatFechaCorta(plazoCierre)}
              {diasParaCierre !== null && (
                <strong className={diasParaCierre <= 15 ? 'text-red-600 ml-1' : 'text-gray-700 ml-1'}>
                  ({diasParaCierre >= 0 ? `${diasParaCierre} días restantes` : 'vencido'})
                </strong>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Datos de la convocatoria + Próximos hitos */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Datos de la convocatoria</h2>
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-t border-gray-100"><td className="py-1.5 text-gray-500">Fecha del acto electoral</td><td className="py-1.5 text-right font-medium">{formatFechaCorta(convocatoria.fechaActo)}</td></tr>
              <tr className="border-t border-gray-100"><td className="py-1.5 text-gray-500">Autoridad convocante</td><td className="py-1.5 text-right font-medium">{convocatoria.autoridadConvocante}</td></tr>
              <tr className="border-t border-gray-100"><td className="py-1.5 text-gray-500">Ámbito</td><td className="py-1.5 text-right font-medium">{AMBITO_LABELS[convocatoria.ambitoTipo] || convocatoria.ambitoTipo}</td></tr>
              <tr className="border-t border-gray-100"><td className="py-1.5 text-gray-500">Cargo</td><td className="py-1.5 text-right font-medium">{convocatoria.cargo} ({convocatoria.cantidadTitulares} titular{convocatoria.cantidadTitulares === 1 ? '' : 'es'})</td></tr>
              <tr className="border-t border-gray-100"><td className="py-1.5 text-gray-500">Sistema</td><td className="py-1.5 text-right font-medium">{SISTEMA_LABELS[convocatoria.sistemaElectoral] || convocatoria.sistemaElectoral}</td></tr>
              {convocatoria.documentoInstrumento && (
                <tr className="border-t border-gray-100">
                  <td className="py-1.5 text-gray-500">Documento</td>
                  <td className="py-1.5 text-right">
                    <button onClick={() => documentoService.download(convocatoria.documentoInstrumento!.id)} className="text-blue-600 hover:text-blue-800 underline">
                      {convocatoria.documentoInstrumento.nombreOriginal}
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Próximos hitos</h2>
          <div className="text-sm">
            {HITOS.map(h => {
              const fecha = convocatoria.plazos?.[h.key];
              const esCierre = h.key === 'cierre_candidaturas';
              const dias = esCierre ? diasRestantes(fecha) : null;
              return (
                <div key={h.key} className="py-2 border-t border-gray-100 first:border-t-0 flex justify-between items-center">
                  <span className="text-gray-600">{h.label}</span>
                  <span className={esCierre ? 'font-semibold text-red-600' : 'text-gray-900'}>
                    {formatFechaCorta(fecha)}
                    {dias !== null && ` (${dias >= 0 ? `${dias} días` : 'vencido'})`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Contadores de habilitación */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 shadow">
          <p className="text-sm text-gray-500">Habilitadas</p>
          <p className="text-2xl font-bold text-green-600">{contadores.habilitadas}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow">
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold text-gray-900">{contadores.total}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow">
          <p className="text-sm text-gray-500">Fuera de ámbito</p>
          <p className="text-2xl font-bold text-gray-600">{contadores.fueraDeAmbito}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow">
          <p className="text-sm text-gray-500">Sin personería vigente</p>
          <p className="text-2xl font-bold text-orange-600">{contadores.sinPersoneria}</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="bg-white rounded-lg shadow p-4 grid grid-cols-1 sm:grid-cols-3 gap-4 flex-1">
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por denominación o sigla..."
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <select
            value={filtroAmbito}
            onChange={(e) => setFiltroAmbito(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Todos los ámbitos</option>
            <option value="MUNICIPAL">Municipal</option>
            <option value="DISTRITO">Distrital</option>
            <option value="PROVINCIAL">Provincial</option>
          </select>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Todas</option>
            <option value="habilitadas">Habilitadas</option>
            <option value="deshabilitadas">Deshabilitadas</option>
          </select>
        </div>
        <button
          onClick={() => setModalAgregarAbierto(true)}
          className="inline-flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2.5 rounded-lg whitespace-nowrap"
        >
          + Agregar agrupación
        </button>
      </div>

      {/* Tabla de habilitación */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Denominación</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Ámbito</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Personería</th>
                <th className="px-4 py-2.5 text-center text-xs font-medium text-gray-500 uppercase">Habilitada</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Motivo</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Última modificación</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {listaFiltrada.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">No se encontraron agrupaciones con los filtros aplicados</td>
                </tr>
              ) : (
                listaFiltrada.map((a) => {
                  const badge = ambitoBadge(a);
                  const bloqueada = a.motivoDeshabilitacion !== null && MOTIVOS_SISTEMA.includes(a.motivoDeshabilitacion);
                  const quien = a.actualizadaPor || a.marcadaPor;
                  const cuando = a.actualizadaEn || a.marcadaEn;
                  return (
                    <tr key={a.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-2.5">
                        <Link
                          to={`/partidos/${a.partidoId}`}
                          state={{ from: '/gaiman/agrupaciones-habilitadas' }}
                          className="group"
                        >
                          <p className="font-medium text-blue-600 group-hover:text-blue-800">{a.denominacion}</p>
                          {a.sigla && <p className="text-xs text-gray-500">{a.sigla}</p>}
                        </Link>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{a.personeria}</td>
                      <td className="px-4 py-2.5 text-center">
                        <input
                          type="checkbox"
                          checked={a.habilitada}
                          disabled={bloqueada}
                          title={bloqueada ? (a.motivoDeshabilitacion || undefined) : undefined}
                          onChange={() => abrirModal(a)}
                          className="h-4 w-4 rounded border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                        />
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{a.motivoDeshabilitacion || '—'}</td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">
                        {quien && cuando ? (
                          <>
                            {quien}<br />
                            {new Date(cuando).toLocaleString('es-AR')}
                          </>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Actividad de las agrupaciones habilitadas */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Actividad de las agrupaciones</h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
          <div className="bg-white rounded-lg p-4 shadow">
            <p className="text-sm text-gray-500">Con borrador iniciado</p>
            <p className="text-2xl font-bold text-amber-600">{actividadContadores.conBorrador}</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow">
            <p className="text-sm text-gray-500">Listas presentadas</p>
            <p className="text-2xl font-bold text-blue-600">{actividadContadores.presentadas}</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow">
            <p className="text-sm text-gray-500">Sin actividad</p>
            <p className="text-2xl font-bold text-gray-500">{actividadContadores.sinActividad}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Agrupación</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Ámbito</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Estado de la lista</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Última actividad</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {habilitadas.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">No hay agrupaciones habilitadas todavía</td>
                  </tr>
                ) : (
                  habilitadas.map(a => {
                    const badge = ambitoBadge(a);
                    const estado = ESTADO_LISTA_INFO[a.estadoLista] || ESTADO_LISTA_INFO.SIN_ACTIVIDAD;
                    return (
                      <tr key={a.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5">
                          <p className="font-medium text-gray-900">{a.denominacion}</p>
                          {a.sigla && <p className="text-xs text-gray-500">{a.sigla}</p>}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${estado.color}`}>
                            {estado.label}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">
                          {a.ultimaActividad ? new Date(a.ultimaActividad).toLocaleString('es-AR') : '—'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal de confirmación */}
      {modalAgrupacion && (
        <Modal
          isOpen={true}
          onClose={() => setModalAgrupacion(null)}
          title={modalHabilitar ? 'Habilitar agrupación' : 'Deshabilitar agrupación'}
          size="md"
        >
          <p className="text-sm text-gray-600 mb-4">
            {modalHabilitar ? 'Vas a habilitar a' : 'Vas a deshabilitar a'} <strong>{modalAgrupacion.denominacion}</strong> para presentar candidaturas en esta convocatoria.
          </p>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Motivo {modalHabilitar ? '(opcional)' : '(obligatorio)'}
          </label>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={modalHabilitar ? 'Opcional' : 'Explicá por qué se deshabilita'}
          />
          <div className="mt-5 flex justify-end gap-3">
            <button
              onClick={() => setModalAgrupacion(null)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={confirmarCambio}
              disabled={guardando}
              className={`px-4 py-2 rounded-lg text-white disabled:opacity-50 ${
                modalHabilitar ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {guardando ? 'Guardando...' : 'Confirmar'}
              </button>
          </div>
        </Modal>
      )}

      {/* Modal: agregar agrupación nueva a la convocatoria */}
      {modalAgregarAbierto && (
        <Modal isOpen={true} onClose={cerrarModalAgregar} title="Agregar agrupación a la convocatoria" size="md">
          <p className="text-sm text-gray-600 mb-3">
            Buscá una agrupación ya reconocida en SIREPPAR que todavía no forme parte de esta convocatoria.
          </p>
          <input
            type="text"
            autoFocus
            value={busquedaModal}
            onChange={(e) => setBusquedaModal(e.target.value)}
            placeholder="Buscar por denominación o sigla..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <div className="mt-3 border border-gray-100 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {cargandoDisponibles ? (
              <p className="px-3.5 py-4 text-sm text-gray-500 text-center">Cargando...</p>
            ) : disponiblesFiltrados.length === 0 ? (
              <p className="px-3.5 py-4 text-sm text-gray-500 text-center">No hay agrupaciones para agregar</p>
            ) : (
              disponiblesFiltrados.map(p => (
                <div key={p.id} className={`flex items-center justify-between px-3.5 py-2.5 ${!p.personeriaValida ? 'opacity-60' : ''}`}>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{p.denominacion}</p>
                    <p className="text-xs text-gray-500">
                      {TIPO_LABELS[p.tipo] || p.tipo} · {p.localidad || '—'} ·{' '}
                      {p.personeriaValida ? 'personería vigente' : <span className="text-orange-600">personería no vigente</span>}
                    </p>
                  </div>
                  {p.personeriaValida ? (
                    <button
                      onClick={() => agregarAgrupacion(p)}
                      disabled={agregandoId === p.id}
                      className="text-xs font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-md whitespace-nowrap"
                    >
                      {agregandoId === p.id ? 'Agregando...' : 'Agregar'}
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400 px-3 py-1.5 whitespace-nowrap">No disponible</span>
                  )}
                </div>
              ))
            )}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Al agregarla, entra deshabilitada por defecto — la habilitás desde la grilla como a cualquier otra.
          </p>
        </Modal>
      )}
    </div>
  );
}
